<script setup>
import NewsMenu from '~/components/news-page/news-menu/news-menu.vue';
import { ref } from '#imports';

const props = defineProps({
  tags: {
    type: Object,
    required: true,
    default: () => Object,
  },
});

const emit = defineEmits(['fetchTag', 'search']);
const isMenuOpened = ref(false);

function toggleMenu() {
  isMenuOpened.value = !isMenuOpened.value;
  document.body.style.overflow = isMenuOpened.value ? 'clip' : 'auto';
}

function handleFetchTag(tag) {
  emit('fetchTag', tag);
}

function handleSearch(query) {
  emit('search', query);
}
</script>

<template>
  <div class="filter">
    <div class="filter__trigger" @click="toggleMenu"></div>
    <div v-if="isMenuOpened" class="filter__menu">
      <NewsMenu
        :tags="props.tags"
        @close="toggleMenu"
        @fetch-tag="handleFetchTag"
        @search="handleSearch"
      />
    </div>
    <div v-if="isMenuOpened" class="filter__overlay" @click="toggleMenu"></div>
  </div>
</template>

<style lang="postcss">
.filter {
  position: sticky;
  margin-right: 20px;
  right: 0;
  bottom: 50px;
  width: 70px;
  padding-left: calc(100% - 90px);
  z-index: calc(var(--modal-z-index) - 1);

  @supports (-webkit-touch-callout: none) {
    bottom: 20px;
  }

  &__trigger {
    background-color: var(--accent-color);
    border-radius: 50%;
    cursor: pointer;
    height: 70px;
    width: 70px;

    &::after {
      content: '';
      position: absolute;
      background-image: url("data:image/svg+xml,%3Csvg width='20' height='19' viewBox='0 0 20 19' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.666667 3.32905H0.761333C0.906848 3.90014 1.2387 4.40653 1.70448 4.7682C2.17025 5.12988 2.74338 5.32623 3.33333 5.32623C3.92328 5.32623 4.49642 5.12988 4.96219 4.7682C5.42796 4.40653 5.75982 3.90014 5.90533 3.32905H19.3333C19.5101 3.32905 19.6797 3.25889 19.8047 3.134C19.9298 3.00911 20 2.83973 20 2.66311C20 2.4865 19.9298 2.31711 19.8047 2.19223C19.6797 2.06734 19.5101 1.99718 19.3333 1.99718H5.90533C5.75982 1.42609 5.42796 0.9197 4.96219 0.558024C4.49642 0.196348 3.92328 0 3.33333 0C2.74338 0 2.17025 0.196348 1.70448 0.558024C1.2387 0.9197 0.906848 1.42609 0.761333 1.99718H0.666667C0.489856 1.99718 0.320286 2.06734 0.195262 2.19223C0.0702379 2.31711 0 2.4865 0 2.66311C0 2.83973 0.0702379 3.00911 0.195262 3.134C0.320286 3.25889 0.489856 3.32905 0.666667 3.32905ZM3.33333 1.33125C3.59704 1.33125 3.85483 1.40936 4.07409 1.55571C4.29336 1.70205 4.46426 1.91006 4.56517 2.15343C4.66609 2.3968 4.69249 2.66459 4.64105 2.92295C4.5896 3.18131 4.46261 3.41862 4.27614 3.60489C4.08967 3.79115 3.8521 3.918 3.59345 3.96939C3.33481 4.02078 3.06672 3.99441 2.82309 3.8936C2.57945 3.79279 2.37122 3.62209 2.22471 3.40306C2.0782 3.18404 2 2.92653 2 2.66311C2 2.30988 2.14048 1.97111 2.39052 1.72134C2.64057 1.47157 2.97971 1.33125 3.33333 1.33125ZM19.3333 8.65652H19.2387C19.0932 8.08543 18.7613 7.57904 18.2955 7.21737C17.8298 6.85569 17.2566 6.65934 16.6667 6.65934C16.0767 6.65934 15.5036 6.85569 15.0378 7.21737C14.572 7.57904 14.2402 8.08543 14.0947 8.65652H0.666667C0.489856 8.65652 0.320286 8.72668 0.195262 8.85157C0.0702379 8.97646 0 9.14584 0 9.32246C0 9.49907 0.0702379 9.66846 0.195262 9.79334C0.320286 9.91823 0.489856 9.98839 0.666667 9.98839H14.0947C14.2402 10.5595 14.572 11.0659 15.0378 11.4275C15.5036 11.7892 16.0767 11.9856 16.6667 11.9856C17.2566 11.9856 17.8298 11.7892 18.2955 11.4275C18.7613 11.0659 19.0932 10.5595 19.2387 9.98839H19.3333C19.5101 9.98839 19.6797 9.91823 19.8047 9.79334C19.9298 9.66846 20 9.49907 20 9.32246C20 9.14584 19.9298 8.97646 19.8047 8.85157C19.6797 8.72668 19.5101 8.65652 19.3333 8.65652ZM16.6667 10.6543C16.403 10.6543 16.1452 10.5762 15.9259 10.4299C15.7066 10.2835 15.5357 10.0755 15.4348 9.83214C15.3339 9.58877 15.3075 9.32098 15.359 9.06262C15.4104 8.80426 15.5374 8.56695 15.7239 8.38068C15.9103 8.19442 16.1479 8.06757 16.4065 8.01618C16.6652 7.96479 16.9333 7.99116 17.1769 8.09197C17.4205 8.19278 17.6288 8.36349 17.7753 8.58251C17.9218 8.80153 18 9.05904 18 9.32246C18 9.67569 17.8595 10.0145 17.6095 10.2642C17.3594 10.514 17.0203 10.6543 16.6667 10.6543ZM19.3333 15.3159H12.572C12.4265 14.7448 12.0946 14.2384 11.6289 13.8767C11.1631 13.515 10.59 13.3187 10 13.3187C9.41005 13.3187 8.83692 13.515 8.37114 13.8767C7.90537 14.2384 7.57351 14.7448 7.428 15.3159H0.666667C0.489856 15.3159 0.320286 15.386 0.195262 15.5109C0.0702379 15.6358 0 15.8052 0 15.9818C0 16.1584 0.0702379 16.3278 0.195262 16.4527C0.320286 16.5776 0.489856 16.6477 0.666667 16.6477H7.428C7.57351 17.2188 7.90537 17.7252 8.37114 18.0869C8.83692 18.4486 9.41005 18.6449 10 18.6449C10.59 18.6449 11.1631 18.4486 11.6289 18.0869C12.0946 17.7252 12.4265 17.2188 12.572 16.6477H19.3333C19.5101 16.6477 19.6797 16.5776 19.8047 16.4527C19.9298 16.3278 20 16.1584 20 15.9818C20 15.8052 19.9298 15.6358 19.8047 15.5109C19.6797 15.386 19.5101 15.3159 19.3333 15.3159ZM10 17.3137C9.73629 17.3137 9.47851 17.2356 9.25924 17.0892C9.03997 16.9429 8.86908 16.7349 8.76816 16.4915C8.66724 16.2481 8.64084 15.9803 8.69229 15.722C8.74373 15.4636 8.87072 15.2263 9.05719 15.04C9.24366 14.8538 9.48124 14.7269 9.73988 14.6755C9.99852 14.6241 10.2666 14.6505 10.5102 14.7513C10.7539 14.8521 10.9621 15.0228 11.1086 15.2419C11.2551 15.4609 11.3333 15.7184 11.3333 15.9818C11.3333 16.335 11.1929 16.6738 10.9428 16.9236C10.6928 17.1733 10.3536 17.3137 10 17.3137Z' fill='white'/%3E%3C/svg%3E%0A");
      background-repeat: no-repeat;
      background-size: auto;
      background-position: center;
      height: 70px;
      width: 70px;
    }
  }

  &__overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: calc(var(--modal-z-index) - 2);
    background-color: rgba(var(--accent-color--rgb), 0.4);
  }

  &__menu {
    position: absolute;
    right: -50px;
    bottom: 20px;
    z-index: calc(var(--modal-z-index) + 2);

    @supports (-webkit-touch-callout: none) {
      bottom: 20px;
    }
  }
}
</style>
